import React, { useContext } from 'react';
import { Spinner } from '@vp/swan';
import { defineMessages } from '@vistaprint-org/digital-i18n-utils';
import { useTranslation } from 'react-i18next';
import SiteModel from '../../models/SiteModel';
import {
  PREVIEW_MESSAGE_TYPES,
  PREVIEW_PROTOCOL_VERSION,
  buildPreviewRuntimeUrl,
  isExpectedPreviewMessageAddress,
  parsePreviewChildEnvelope,
  parsePreviewRevision,
  parsePreviewSessionId,
  type PreviewSessionId,
  type PreviewUpdateEnvelope,
} from './previewProtocol';

import { SessionContext } from '../hooks/SessionContext';
import ErrorContext from '../utils/ErrorContext';

import './PreviewIframe.scss';

interface PreviewIFrameProps {
  previewParams: SiteModel;
}

const READY_TIMEOUT_MS = 10_000;

const messages = defineMessages({
  loadingAria: 'Loading',
});

interface LivePreviewIFrameProps extends PreviewIFrameProps {
  sessionId: PreviewSessionId;
}

const LivePreviewIFrame: React.FC<LivePreviewIFrameProps> = ({
  sessionId,
  previewParams,
}: LivePreviewIFrameProps) => {
  const { t } = useTranslation();
  const { userData } = useContext(SessionContext);
  const { setShowAlert } = useContext(ErrorContext);
  const altId = userData.connectedProduct.altId;
  const studioData = userData.connectedProduct.studioData;
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const latestRef = React.useRef({ studioData, previewParams });
  const isChildReadyRef = React.useRef(false);
  const revisionRef = React.useRef(0);
  const [previewSrc, setPreviewSrc] = React.useState<string | null>(null);
  const [isRevealed, setIsRevealed] = React.useState(false);

  const postLatest = React.useCallback((): void => {
    const target = iframeRef.current?.contentWindow;
    if (!target || !isChildReadyRef.current) {
      return;
    }
    const revision = parsePreviewRevision(revisionRef.current + 1);
    if (revision === null) {
      return;
    }
    revisionRef.current += 1;
    const { studioData: latestStudioData, previewParams: latestParams } = latestRef.current;
    const envelope: PreviewUpdateEnvelope = {
      type: PREVIEW_MESSAGE_TYPES.update,
      version: PREVIEW_PROTOCOL_VERSION,
      sessionId,
      revision,
      siteModel: [latestStudioData, ...latestParams],
    };
    target.postMessage(envelope, window.location.origin);
  }, [sessionId]);

  React.useEffect(() => {
    const readyTimeout = window.setTimeout(() => {
      if (isChildReadyRef.current) {
        return;
      }
      setShowAlert(true);
      setIsRevealed(true);
    }, READY_TIMEOUT_MS);
    const onMessage = (event: MessageEvent<unknown>): void => {
      const childWindow = iframeRef.current?.contentWindow;
      if (
        !childWindow ||
        !isExpectedPreviewMessageAddress(
          { source: event.source, origin: event.origin },
          { source: childWindow, origin: window.location.origin }
        )
      ) {
        return;
      }
      const envelope = parsePreviewChildEnvelope(event.data, sessionId);
      if (envelope === null) {
        return;
      }
      if (envelope.type === PREVIEW_MESSAGE_TYPES.ready) {
        isChildReadyRef.current = true;
        window.clearTimeout(readyTimeout);
        postLatest();
      } else if (envelope.type === PREVIEW_MESSAGE_TYPES.committed) {
        setIsRevealed(true);
      } else {
        setShowAlert(true);
        setIsRevealed(true);
      }
    };
    // Listener first, then the document that will announce ready.
    window.addEventListener('message', onMessage);
    setPreviewSrc(
      buildPreviewRuntimeUrl({
        altId,
        sessionId,
        currentHref: window.location.href,
      })
    );
    return () => {
      window.clearTimeout(readyTimeout);
      window.removeEventListener('message', onMessage);
    };
  }, [altId, sessionId, postLatest, setShowAlert]);

  React.useEffect(() => {
    latestRef.current = { studioData, previewParams };
    postLatest();
  }, [studioData, previewParams, postLatest]);

  return (
    <div className="site-preview-frame-container">
      <div className="mobile-device-element mobile-up-volume" />
      <div className="mobile-device-element mobile-down-volume" />
      <div className="mobile-device-element mobile-lock" />
      <div className="mobile-device-element mobile-speaker" />
      <div className="mobile-device-element mobile-camera" />
      {!isRevealed && (
        <div className="site-preview-frame-overlay">
          <Spinner accessibleText={t(messages.loadingAria.id)} />
        </div>
      )}
      {previewSrc !== null && (
        <iframe
          ref={iframeRef}
          className="site-preview-frame"
          title={'sitePreviewIframe'}
          src={previewSrc}
          style={isRevealed ? undefined : { visibility: 'hidden' }}
        />
      )}
    </div>
  );
};

const PreviewIFrame: React.FC<PreviewIFrameProps> = (props: PreviewIFrameProps) => {
  const { t } = useTranslation();
  const { setShowAlert } = useContext(ErrorContext);
  const [sessionId] = React.useState(() => parsePreviewSessionId(crypto.randomUUID()));

  React.useEffect(() => {
    if (sessionId === null) {
      setShowAlert(true);
    }
  }, [sessionId, setShowAlert]);

  if (sessionId === null) {
    return (
      <div className="site-preview-frame-container">
        <div className="site-preview-frame-overlay">
          <Spinner accessibleText={t(messages.loadingAria.id)} />
        </div>
      </div>
    );
  }
  return <LivePreviewIFrame {...props} sessionId={sessionId} />;
};

export default PreviewIFrame;
